import React, { useEffect, useState } from "react";
import {
  MdOutlineUndo,
  MdRedo,
  MdFormatBold,
  MdFormatItalic,
  MdFormatUnderlined,
  MdOutlineFormatListBulleted,
  MdOutlineFormatListNumbered,
  MdFormatClear,
  MdDataObject,
  MdOutlineExpandMore,
  MdDeveloperMode,
  MdOutlineEmojiEmotions,
} from "react-icons/md";
import Highlight from "@tiptap/extension-highlight";
import Typography from "@tiptap/extension-typography";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import Placeholder from "@tiptap/extension-placeholder";
import Code from "@tiptap/extension-code";
import Emoji, { gitHubEmojis } from "@tiptap/extension-emoji";
import EmojiPicker from "./EmojiPicker";
import { defaultCodeTailwindStyle } from "config/default";
import ColourPicker from "./ColourPicker";

interface MarkdownInputFieldProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
}

export const MarkdownInputField: React.FC<MarkdownInputFieldProps> = ({
  value,
  onChange,
  placeholder,
  className = "w-full bg-neutral-50 rounded-lg",
}) => {
  // States definition
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showEmojisPicker, setShowEmojisPicker] = useState(false);

  /**
   * Define the custom version of the code block defined previously
   */
  const CustomCode = Code.extend({
    excludes: "",
    renderHTML({ HTMLAttributes }) {
      return [
        "code",
        {
          ...HTMLAttributes,
          class: defaultCodeTailwindStyle,
        },
        0,
      ];
    },
  });

  // Editor definition
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: false, // Disable code, it'll be replaced by CustomCode
      }),
      Highlight.configure({
        multicolor: true,
      }),
      Typography,
      TextStyle,
      Color,
      CustomCode,
      Placeholder.configure({ placeholder }),
      Emoji.configure({
        emojis: gitHubEmojis,
        enableEmoticons: true,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      if (onChange) {
        const html = editor.getHTML();
        onChange(html);
      }
    },
    editorProps: {
      attributes: {
        class:
          "w-full h-full text-neutral-700 prose prose-sm p-3 pt-0 focus:outline-none [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:ml-4 break-words",
      },
    },
  });

  /**
   * Hook that controls the content of this input in the
   * we update the state that controls the text shown on
   * this component from the parent/outside
   */
  useEffect(() => {
    // Check if the editor is defined
    if (!editor) return;

    // Get the current value
    const current = editor.getHTML();

    // Prevent infinite update loops
    if (value !== undefined && value !== current) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  /**
   * Toolbar button definition
   * @param onClick
   * @param active
   * @param children
   * @param title
   * @returns
   */
  const ToolbarButton = ({
    onClick,
    active,
    children,
    title,
  }: {
    onClick: () => void;
    active?: boolean;
    children?: React.ReactNode;
    title: string;
  }) => (
    <button
      tabIndex={-1}
      onClick={onClick}
      className={`rounded-md h-fit hover:bg-neutral-100 transition-colors 
        ${active ? "bg-neutral-200" : ""}`}
      title={title}
      type="button"
    >
      {children}
    </button>
  );

  return (
    <div className={className}>
      <style>{`
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #999999;
          pointer-events: none;
          height: 0;
        }
      `}</style>
      <div className="p-3 inline-flex items-center flex-shrink-0 gap-3">
        <ToolbarButton
          data-style="Round"
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
        >
          <MdOutlineUndo className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
        >
          <MdRedo className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <div className="self-stretch flex justify-start items-start gap-2">
          <div className="w-px self-stretch bg-neutral-200" />
        </div>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
        >
          <MdFormatBold className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
        >
          <MdFormatItalic className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("Underline")}
          title="Underline"
        >
          <MdFormatUnderlined className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <div className="relative">
          <button
            tabIndex={-1}
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="rounded hover:bg-neutral-200 transition-colors flex items-center gap-1"
            title="Text Color"
            type="button"
          >
            <MdOutlineExpandMore className="w-4 fill-neutral-500" />
          </button>

          {showColorPicker && (
            <ColourPicker
              onClose={() => setShowColorPicker(false)}
              onTextColourSelect={(value: string) => {
                editor.chain().focus().setColor(value).run();
                setShowColorPicker(false);
              }}
              onTextColourClear={() => {
                editor.chain().focus().unsetMark("textStyle").run();
                setShowColorPicker(false);
              }}
              onHighlightColourSelect={(value: string) => {
                editor.chain().focus().setHighlight({ color: value }).run();
                setShowColorPicker(false);
              }}
              onHighlightColourClear={() => {
                editor.chain().focus().unsetHighlight().run();
                setShowColorPicker(false);
              }}
            />
          )}
        </div>

        <div className="self-stretch flex justify-start items-start gap-2">
          <div className="w-px self-stretch bg-neutral-200" />
        </div>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleBulletList().run();
          }}
          active={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <MdOutlineFormatListBulleted className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Numbered List"
        >
          <MdOutlineFormatListNumbered className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <div className="self-stretch flex justify-start items-start gap-2">
          <div className="w-px self-stretch bg-neutral-200" />
        </div>

        <ToolbarButton
          onClick={() => editor.chain().focus().clearNodes().run()}
          title="ClearNodes"
        >
          <MdFormatClear className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <div className="self-stretch flex justify-start items-start gap-2">
          <div className="w-px self-stretch bg-neutral-200" />
        </div>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive("code")}
          title="Code"
        >
          <MdDataObject className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive("codeBlock")}
          title="Code Block"
        >
          <MdDeveloperMode className="size-6 fill-neutral-500" />
        </ToolbarButton>

        <div className="self-stretch flex justify-start items-start gap-2">
          <div className="w-px self-stretch bg-neutral-200" />
        </div>

        <div className="relative inline-flex items-center overflow-visible">
          <ToolbarButton
            title="Emojis"
            onClick={() => setShowEmojisPicker((v) => !v)}
          >
            <MdOutlineEmojiEmotions className="size-6 fill-neutral-500" />
          </ToolbarButton>
          {/* Pop up */}
          {showEmojisPicker && (
            <EmojiPicker
              onClose={() => setShowEmojisPicker(false)}
              onSelect={(emoji: string) => {
                editor.chain().focus().insertContent(emoji).run();

                setShowEmojisPicker(false);
              }}
            />
          )}
        </div>
      </div>

      <EditorContent
        editor={editor}
        className=" text-neutral-700 font-sans leading-5 overflow-y-auto w-full h-32"
      />
    </div>
  );
};

export default MarkdownInputField;
